/**
 * A minimal .xlsx writer — one sheet, a bold header row, text/number/boolean
 * cells. Pure: bytes in the browser, bytes in the test, no DOM.
 *
 * ── WHY IT IS HERE AND NOT A PACKAGE ─────────────────────────────────────────
 * The admin client carries no spreadsheet library, and the one thing the back
 * office has ever needed from one is «give me these rows in Excel». An .xlsx is a
 * ZIP of five small XML parts; writing them is ~100 lines and a dependency is a
 * supply-chain surface plus a bundle cost for every screen. If the panel ever
 * needs formulas, several sheets or styles beyond a bold header, replace this
 * with a library rather than growing it.
 *
 * ── DECISIONS THAT LOOK ARBITRARY ────────────────────────────────────────────
 *   • STRINGS ARE `inlineStr`, never shared strings: one fewer part, and Excel
 *     reads them as TEXT — so a phone number keeps its leading zero and a clinic
 *     named «=1+1» is a name, not a formula. (A CSV has neither guarantee.)
 *   • THE ZIP IS STORED, NOT DEFLATED (method 0): a few hundred rows compress to
 *     nothing worth a decoder, and a stored entry needs no zlib in the browser.
 *   • THE ENTRY TIMESTAMP IS FIXED (1980-01-01), so the same rows give the same
 *     bytes — a test can compare them.
 *   • `rtl` flips the SHEET (column A on the right), which is what a Persian
 *     reader expects of a table; the cell text itself is unaffected.
 */

export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const encoder = new TextEncoder();

// ── ZIP (stored) ─────────────────────────────────────────────────────────────

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32(bytes) {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i += 1) c = CRC_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
  return (c ^ 0xFFFFFFFF) >>> 0;
}

// 1980-01-01 00:00:00 in MS-DOS date/time.
const DOS_DATE = (0 << 9) | (1 << 5) | 1;
const DOS_TIME = 0;
// Bit 11: file names are UTF-8.
const FLAGS = 0x0800;

/** @param {{name: string, data: Uint8Array}[]} files */
export function zipStore(files) {
  const parts = [];
  const central = [];
  let offset = 0;

  for (const { name, data } of files) {
    const nameBytes = encoder.encode(name);
    const crc = crc32(data);

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034B50, true);
    local.setUint16(4, 20, true);        // version needed
    local.setUint16(6, FLAGS, true);
    local.setUint16(8, 0, true);         // method: stored
    local.setUint16(10, DOS_TIME, true);
    local.setUint16(12, DOS_DATE, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, data.length, true);
    local.setUint32(22, data.length, true);
    local.setUint16(26, nameBytes.length, true);
    local.setUint16(28, 0, true);        // extra length
    parts.push(new Uint8Array(local.buffer), nameBytes, data);

    const entry = new DataView(new ArrayBuffer(46));
    entry.setUint32(0, 0x02014B50, true);
    entry.setUint16(4, 20, true);        // version made by
    entry.setUint16(6, 20, true);        // version needed
    entry.setUint16(8, FLAGS, true);
    entry.setUint16(10, 0, true);
    entry.setUint16(12, DOS_TIME, true);
    entry.setUint16(14, DOS_DATE, true);
    entry.setUint32(16, crc, true);
    entry.setUint32(20, data.length, true);
    entry.setUint32(24, data.length, true);
    entry.setUint16(28, nameBytes.length, true);
    // 30 extra, 32 comment, 34 disk, 36 internal attrs, 38 external attrs: all 0
    entry.setUint32(42, offset, true);   // local header offset
    central.push(new Uint8Array(entry.buffer), nameBytes);

    offset += 30 + nameBytes.length + data.length;
  }

  const centralSize = central.reduce((sum, p) => sum + p.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054B50, true);
  end.setUint16(8, files.length, true);   // entries on this disk
  end.setUint16(10, files.length, true);  // entries in total
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);        // where the central directory starts

  const all = [...parts, ...central, new Uint8Array(end.buffer)];
  const out = new Uint8Array(all.reduce((sum, p) => sum + p.length, 0));
  let at = 0;
  for (const p of all) { out.set(p, at); at += p.length; }
  return out;
}

// ── OOXML parts ──────────────────────────────────────────────────────────────

const XML_HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
const NS_MAIN = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const NS_REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const NS_PKG_REL = 'http://schemas.openxmlformats.org/package/2006/relationships';

// Characters XML 1.0 cannot carry at all (C0 controls other than tab/LF/CR, and
// the two non-characters). One of these in a clinic's name would make the whole
// file «corrupt» to Excel, so they are dropped rather than escaped.
// eslint-disable-next-line no-control-regex
const XML_ILLEGAL = /[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g;

export function escapeXml(value) {
  return String(value)
    .replace(XML_ILLEGAL, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** 0 → A, 25 → Z, 26 → AA. */
export function columnName(index) {
  let n = index;
  let name = '';
  do {
    name = String.fromCharCode(65 + (n % 26)) + name;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return name;
}

// Excel refuses a sheet name over 31 characters or containing : \ / ? * [ ]
// (and one that is empty), and answers with a «repair» prompt, not an error.
export function safeSheetName(name) {
  const cleaned = String(name || '').replace(/[:\\/?*[\]]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 31);
  return cleaned || 'Sheet1';
}

function cellXml(ref, value, style) {
  if (value === null || value === undefined || value === '') return '';
  const s = style ? ` s="${style}"` : '';
  if (typeof value === 'number' && Number.isFinite(value)) return `<c r="${ref}"${s}><v>${value}</v></c>`;
  if (typeof value === 'boolean') return `<c r="${ref}"${s} t="b"><v>${value ? 1 : 0}</v></c>`;
  const text = String(value);
  const keep = /^\s|\s$|\n/.test(text) ? ' xml:space="preserve"' : '';
  return `<c r="${ref}"${s} t="inlineStr"><is><t${keep}>${escapeXml(text)}</t></is></c>`;
}

const cellLength = (value) => (value == null ? 0 : String(value).length);

function sheetXml({ header, rows, rtl }) {
  const widths = header.map((h, c) => {
    // A reduce, not `Math.max(...rows)`: spreading thousands of rows into one call
    // overflows the argument limit.
    const longest = rows.reduce((max, r) => Math.max(max, cellLength(r[c])), cellLength(h) + 2);
    return Math.min(48, Math.max(8, longest + 2));
  });
  const cols = widths
    .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`)
    .join('');

  const headerRow = `<row r="1">${header.map((h, c) => cellXml(`${columnName(c)}1`, h, 1)).join('')}</row>`;
  const bodyRows = rows.map((row, r) => {
    const n = r + 2;
    return `<row r="${n}">${row.map((v, c) => cellXml(`${columnName(c)}${n}`, v)).join('')}</row>`;
  });

  return `${XML_HEAD}<worksheet xmlns="${NS_MAIN}">`
    + `<sheetViews><sheetView tabSelected="1" workbookViewId="0"${rtl ? ' rightToLeft="1"' : ''}>`
    + '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>'
    + '<selection pane="bottomLeft"/></sheetView></sheetViews>'
    + `<sheetFormatPr defaultRowHeight="15"/><cols>${cols}</cols>`
    + `<sheetData>${headerRow}${bodyRows.join('')}</sheetData></worksheet>`;
}

const STYLES_XML = `${XML_HEAD}<styleSheet xmlns="${NS_MAIN}">`
  + '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>'
  + '<font><b/><sz val="11"/><name val="Calibri"/></font></fonts>'
  + '<fills count="2"><fill><patternFill patternType="none"/></fill>'
  + '<fill><patternFill patternType="gray125"/></fill></fills>'
  + '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>'
  + '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'
  + '<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'
  + '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>'
  + '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>'
  + '</styleSheet>';

const CONTENT_TYPES_XML = `${XML_HEAD}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
  + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
  + '<Default Extension="xml" ContentType="application/xml"/>'
  + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
  + '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>'
  + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
  + '</Types>';

const ROOT_RELS_XML = `${XML_HEAD}<Relationships xmlns="${NS_PKG_REL}">`
  + `<Relationship Id="rId1" Type="${NS_REL}/officeDocument" Target="xl/workbook.xml"/></Relationships>`;

const WORKBOOK_RELS_XML = `${XML_HEAD}<Relationships xmlns="${NS_PKG_REL}">`
  + `<Relationship Id="rId1" Type="${NS_REL}/worksheet" Target="worksheets/sheet1.xml"/>`
  + `<Relationship Id="rId2" Type="${NS_REL}/styles" Target="styles.xml"/></Relationships>`;

const workbookXml = (sheetName) => `${XML_HEAD}<workbook xmlns="${NS_MAIN}" xmlns:r="${NS_REL}">`
  + `<sheets><sheet name="${escapeXml(sheetName)}" sheetId="1" r:id="rId1"/></sheets></workbook>`;

/**
 * @param {object}   spec
 * @param {string}   [spec.sheetName]
 * @param {string[]} spec.header   the bold first row
 * @param {Array<Array<string|number|boolean|null>>} spec.rows
 * @param {boolean}  [spec.rtl]    column A on the right
 * @returns {Uint8Array} the file
 */
export function buildXlsx({ sheetName = 'Sheet1', header, rows, rtl = false }) {
  const part = (name, text) => ({ name, data: encoder.encode(text) });
  return zipStore([
    part('[Content_Types].xml', CONTENT_TYPES_XML),
    part('_rels/.rels', ROOT_RELS_XML),
    part('xl/workbook.xml', workbookXml(safeSheetName(sheetName))),
    part('xl/_rels/workbook.xml.rels', WORKBOOK_RELS_XML),
    part('xl/styles.xml', STYLES_XML),
    part('xl/worksheets/sheet1.xml', sheetXml({ header, rows, rtl })),
  ]);
}
